import { ref } from "vue";
import { type ChatStateThread, useChatStore } from "@/store/chat.store";
import { useAppStore } from "@/store/app.store";
import type { ThreadDto } from "@/api/chat/dto/thread.dto";
import type { CachedThread } from "@/types/chat";
import { LocalStorageKeys } from "@/constants/storageKeys";

const DB_NAME = "userCache";
const THREADS_STORE_NAME = "threads";
const COMPANY_ID_INDEX = "companyId";
const DB_VERSION = 2;

let db: IDBDatabase | null = null;

function getCompanyId(): number | null {
  return useAppStore().companyId;
}

function getThreadsTimeFromKey(companyId: number): string {
  return `${LocalStorageKeys.threadsTimeFrom}_${companyId}`;
}

function clearThreadsTimeFrom(): void {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(LocalStorageKeys.threadsTimeFrom))
    .forEach((key) => localStorage.removeItem(key));
}

async function openDB(): Promise<IDBDatabase> {
  if (db) {
    return db;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onblocked = () => {
      console.error("Обновление кеша тредов заблокировано вкладкой со старой версией приложения");
      reject(new Error("IndexedDB upgrade is blocked by another tab"));
    };
    request.onsuccess = () => {
      const database = request.result;

      database.onversionchange = () => {
        database.close();

        if (db === database) {
          db = null;
        }
      };

      db = database;
      resolve(database);
    };
    request.onupgradeneeded = (event) => {
      const database = request.result;

      if (!database.objectStoreNames.contains(THREADS_STORE_NAME)) {
        const createdStore = database.createObjectStore(THREADS_STORE_NAME, { keyPath: "id" });
        createdStore.createIndex(COMPANY_ID_INDEX, COMPANY_ID_INDEX);
        return;
      }

      const transaction = request.transaction;
      if (!transaction) {
        return;
      }

      const store = transaction.objectStore(THREADS_STORE_NAME);

      if (!store.indexNames.contains(COMPANY_ID_INDEX)) {
        store.createIndex(COMPANY_ID_INDEX, COMPANY_ID_INDEX);
      }

      if (event.oldVersion < 2) {
        store.clear();
        clearThreadsTimeFrom();
      }
    };
  });
}

async function readCompanyThreads(companyId: number): Promise<ChatStateThread[]> {
  const database = await openDB();
  const tx = database.transaction(THREADS_STORE_NAME, "readonly");
  const index = tx.objectStore(THREADS_STORE_NAME).index(COMPANY_ID_INDEX);

  return new Promise<ChatStateThread[]>((resolve, reject) => {
    const request = index.getAll(companyId);

    request.onsuccess = () => resolve(request.result as ChatStateThread[]);
    request.onerror = () => reject(request.error);
  });
}

export function useCache() {
  const cachedThreads = ref<ChatStateThread[]>([]);

  const load = async (): Promise<ChatStateThread[]> => {
    const companyId = getCompanyId();

    cachedThreads.value = companyId ? await readCompanyThreads(companyId) : [];

    return cachedThreads.value;
  };

  const setUseCache = (value: boolean) => {
    localStorage.setItem(LocalStorageKeys.useCache, JSON.stringify(value));
  };

  const getUseCache = () => {
    // return JSON.parse(localStorage.getItem(LocalStorageKeys.useCache) || "false"); // we use cache by default
    return true; // we use cache by default
  };

  const setThreadsTimeFrom = (date: string): void => {
    const companyId = getCompanyId();

    if (!companyId) {
      return;
    }

    localStorage.setItem(getThreadsTimeFromKey(companyId), JSON.stringify(date));
  };

  const getThreadsTimeFrom = (): string => {
    const companyId = getCompanyId();

    if (!companyId) {
      return "";
    }

    const unparsedDate = localStorage.getItem(getThreadsTimeFromKey(companyId));

    if (unparsedDate) {
      return JSON.parse(unparsedDate);
    }

    return "";
  };

  const getCachedThreads = async (updateStore = true): Promise<ChatStateThread[]> => {
    const companyId = getCompanyId();

    if (!companyId) {
      return [];
    }

    try {
      const threads = await readCompanyThreads(companyId);

      if (updateStore) {
        const chatStore = useChatStore();

        chatStore.addThreads(threads);
        chatStore.sortThreads();
      }

      return threads;
    } catch (error) {
      console.error("Ошибка при чтении тредов из кеша:", error);
      throw error;
    }
  };

  const clearCachedThreads = async (storeName = THREADS_STORE_NAME): Promise<boolean> => {
    try {
      const db = await openDB();
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);
      store.clear();

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => {
          resolve();

          if (storeName === THREADS_STORE_NAME) {
            clearThreadsTimeFrom();
          }
        };
        tx.onerror = () => {
          reject(tx.error);

          return false;
        };
      });

      console.log("Кэш тредов успешно очищен");
      return true;
    } catch (error) {
      console.error("Ошибка при очистке базы IndexedDB:", error);
      return false;
    }
  };

  const upsertCachedThread = async (thread: ThreadDto, updateStore = true): Promise<ThreadDto | undefined> => {
    const companyId = getCompanyId();

    if (!companyId) {
      console.error("Тред не сохранён в кеш: не определена компания текущего диспетчера");

      if (updateStore) {
        useChatStore().upsertThread(thread);
      }

      return thread;
    }

    const database = await openDB();
    const tx = database.transaction(THREADS_STORE_NAME, "readwrite");
    const store = tx.objectStore(THREADS_STORE_NAME);
    const cachedThread: CachedThread = { ...JSON.parse(JSON.stringify(thread)), companyId };

    store.put(cachedThread);

    return new Promise<ThreadDto | undefined>((resolve, reject) => {
      tx.oncomplete = () => {
        if (updateStore) {
          const chatStore = useChatStore();
          chatStore.upsertThread(thread);
        }

        return resolve(thread);
      };
      tx.onerror = () => {
        console.error(tx.error);
        reject(tx.error);
        return undefined;
      };
    });
  };

  return {
    cachedThreads,
    load,
    getCachedThreads,
    upsertCachedThread,
    setThreadsTimeFrom,
    getThreadsTimeFrom,
    clearCachedThreads,
    setUseCache,
    getUseCache,
  };
}
